import VisitItem from "@/app/components/molecules/VisitItem";
import TextLink from "@/app/components/atoms/TextLink";
import Button from "@/app/components/atoms/Button";
import type { VisitData } from "@/app/components/types";

interface VisitsListProps {
  visits: VisitData[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  seeAllHref?: string;
}

/**
 * @component VisitsList
 * @description Displays a list of upcoming booked visits inside a bordered card,
 * with a "See all visits" link at the bottom. Handles loading, error, and empty states.
 * @param visits - Array of booked visits to render.
 * @param loading - When true, renders an animated skeleton instead of the list.
 * @param error - Error message to display in the error state.
 * @param onRetry - Callback for the "Try again" button shown in the error state.
 * @param seeAllHref - Internal route for the "See all visits" link. Defaults to `"/visits"`.
 * @returns A `<section>` containing the visits list and a "See all" link.
 */
export default function VisitsList({
  visits,
  loading = false,
  error = null,
  onRetry,
  seeAllHref = "/visits",
}: VisitsListProps) {
  return (
    <section aria-labelledby="booked-visits-heading" className="flex flex-col">
      <div className="p-6 bg-white border border-card-stroke">
        <h2
          id="booked-visits-heading"
          className="text-heading text-charcoal mb-6"
        >
          Booked Visits
        </h2>

        {loading ? (
          <ul aria-busy="true" aria-label="Loading booked visits">
            {Array.from({ length: 3 }).map((_, i) => (
              <li
                key={i}
                className="border-b border-card-stroke last:border-b-0 py-6 animate-pulse"
              >
                <div className="flex items-start gap-3">
                  <div className="h-4 bg-gray-200 w-2/5" />
                  <div className="ml-auto text-right space-y-1.5">
                    <div className="h-4 bg-gray-200 w-20" />
                    <div className="h-3 bg-gray-200 w-16 ml-auto" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <div className="py-4 flex flex-col items-center gap-3">
            <p className="text-body-base text-muted-text text-center">
              {error}
            </p>
            {onRetry && (
              <Button variant="primary" size="compact" onClick={onRetry}>
                Try again
              </Button>
            )}
          </div>
        ) : visits.length === 0 ? (
          <p className="text-body-base text-muted-text py-4">
            No upcoming visits booked yet.
          </p>
        ) : (
          <ul role="list">
            {visits.map((visit, index) => (
              <li
                key={`${visit.name}-${index}`}
                className="border-b border-card-stroke last:border-b-0"
              >
                <VisitItem {...visit} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex justify-end mt-4">
        <TextLink as="a" href={seeAllHref} arrow>
          See All Visits
        </TextLink>
      </div>
    </section>
  );
}
